import { useCallback, useEffect, useRef } from 'react';

const PIN_TO_BOTTOM_THRESHOLD_PX = 32;
const PROGRAMMATIC_SCROLL_GUARD_MS = 600;

export type UseAutoscrollResult = {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  endAnchorRef: React.RefObject<HTMLDivElement>;
  scrollToBottomIfPinned: () => void;
  scrollToBottom: () => void;
  scrollElementToTop: (element: HTMLElement | null) => void;
  scrollElementToTopIfPinned: (element: HTMLElement | null) => void;
};

const prefersReducedMotion = (): boolean => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

const computeIsPinnedToBottom = (container: HTMLElement): boolean => {
  const distanceFromBottom =
    container.scrollHeight - container.scrollTop - container.clientHeight;
  return distanceFromBottom <= PIN_TO_BOTTOM_THRESHOLD_PX;
};

/**
 * Pinned-to-bottom autoscroll for `MessageList`.
 *
 * @example
 * const { scrollContainerRef, endAnchorRef, scrollToBottomIfPinned } =
 *   useAutoscroll();
 *
 * useEffect(() => {
 *   scrollToBottomIfPinned();
 * }, [messages.length, isStreaming, scrollToBottomIfPinned]);
 *
 * return (
 *   <div ref={scrollContainerRef}>
 *     {messages.map(...)}
 *     <div ref={endAnchorRef} />
 *   </div>
 * );
 */
function useAutoscroll(isStreaming = false): UseAutoscrollResult {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const endAnchorRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);
  const ignoreScrollUntilRef = useRef(0);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container === null) {
      return undefined;
    }
    const handleScroll = () => {
      if (Date.now() < ignoreScrollUntilRef.current) {
        return;
      }
      isPinnedRef.current = computeIsPinnedToBottom(container);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const getScrollBehavior = useCallback(
    (): ScrollBehavior =>
      prefersReducedMotion() || isStreaming ? 'auto' : 'smooth',
    [isStreaming]
  );

  const guardProgrammaticScroll = useCallback((behavior: ScrollBehavior) => {
    if (behavior !== 'auto') {
      ignoreScrollUntilRef.current = Date.now() + PROGRAMMATIC_SCROLL_GUARD_MS;
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    const anchor = endAnchorRef.current;
    if (anchor === null) {
      return;
    }
    const behavior = getScrollBehavior();
    isPinnedRef.current = true;
    guardProgrammaticScroll(behavior);
    anchor.scrollIntoView({
      behavior,
      block: 'end',
    });
  }, [getScrollBehavior, guardProgrammaticScroll]);

  const scrollToBottomIfPinned = useCallback(() => {
    if (!isPinnedRef.current) {
      return;
    }
    scrollToBottom();
  }, [scrollToBottom]);

  const scrollElementToTop = useCallback((element: HTMLElement | null) => {
    const container = scrollContainerRef.current;
    if (container === null || element === null) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    container.scrollTop += elementRect.top - containerRect.top;
    isPinnedRef.current = computeIsPinnedToBottom(container);
  }, []);

  const scrollElementToTopIfPinned = useCallback(
    (element: HTMLElement | null) => {
      if (!isPinnedRef.current) {
        return;
      }
      scrollElementToTop(element);
    },
    [scrollElementToTop]
  );

  return {
    scrollContainerRef,
    endAnchorRef,
    scrollToBottomIfPinned,
    scrollToBottom,
    scrollElementToTop,
    scrollElementToTopIfPinned,
  };
}

export default useAutoscroll;
